// src/products/dataroom/components/SetupWizard/TeamSetupStep.js
import React, { useState, useEffect } from 'react';
import { Plus, Users, Trash2, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import partyTeamService from '../../services/partyTeamService';

const TeamSetupStep = ({ projectId, setupData, onComplete }) => {
  const [partyTeams, setPartyTeams] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedParties, setExpandedParties] = useState({});

  const permissionTypes = partyTeamService.getPermissionTypes();

  useEffect(() => {
    // Initialize teams for each party
    if (setupData.parties && setupData.parties.length > 0) {
      const initialTeams = {};
      const initialExpanded = {};
      let needsUpdate = false;
      
      setupData.parties.forEach(party => {
        if (!partyTeams[party.id]) {
          initialTeams[party.id] = [];
          needsUpdate = true;
        }
        // Expand first party by default
        if (setupData.parties.indexOf(party) === 0 && !expandedParties[party.id]) {
          initialExpanded[party.id] = true;
        }
      });
      
      if (needsUpdate) {
        setPartyTeams(prev => ({ ...prev, ...initialTeams }));
      }
      if (Object.keys(initialExpanded).length > 0) {
        setExpandedParties(prev => ({ ...prev, ...initialExpanded }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupData.parties]); // Intentionally excluding partyTeams and expandedParties to prevent infinite loop

  const addTeamToParty = (partyId, partyName) => {
    const teamName = prompt(`Enter team name for ${partyName}:`);
    if (!teamName || !teamName.trim()) return;

    const newTeam = {
      id: `temp_${Date.now()}`,
      name: teamName.trim(),
      party_id: partyId,
      team_type: 'general',
      permissions: {
        files: 'view', // Default file permission
        questions: 'team_questions_only' // Default question permission
      }
    };

    setPartyTeams(prev => ({
      ...prev,
      [partyId]: [...(prev[partyId] || []), newTeam]
    }));
  };

  const togglePartyExpansion = (partyId) => {
    setExpandedParties(prev => ({
      ...prev,
      [partyId]: !prev[partyId]
    }));
  };

  const removeTeamFromParty = (partyId, teamId) => {
    setPartyTeams(prev => ({
      ...prev,
      [partyId]: prev[partyId].filter(team => team.id !== teamId)
    }));
  };

  const updateTeamPermissions = (partyId, teamId, permissionType, value) => {
    setPartyTeams(prev => ({
      ...prev,
      [partyId]: prev[partyId].map(team => 
        team.id === teamId 
          ? { ...team, permissions: { ...team.permissions, [permissionType]: value } }
          : team
      )
    }));
  };

  const handleContinue = async () => {
    // Check if all parties have at least one team
    const partiesWithoutTeams = setupData.parties.filter(party => 
      !partyTeams[party.id] || partyTeams[party.id].length === 0
    );

    if (partiesWithoutTeams.length > 0) {
      setError(`Each party must have at least one team. Please add teams for: ${partiesWithoutTeams.map(p => p.name).join(', ')}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const createdTeams = {};

      // Create teams for each party
      for (const party of setupData.parties) {
        const partyTeamsList = partyTeams[party.id] || [];
        createdTeams[party.id] = [];

        for (const team of partyTeamsList) {
          const result = await partyTeamService.createTeam(party.id, {
            name: team.name,
            description: team.description || '',
            team_type: team.team_type
          });

          if (result.error) {
            throw new Error(`Failed to create team "${team.name}": ${result.error.message}`);
          }

          createdTeams[party.id].push(result.data);
        }
      }

      onComplete({ teams: createdTeams });
    } catch (err) {
      console.error('Error creating teams:', err);
      setError(err.message || 'Failed to create teams');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Parties and Teams */}
      <div className="space-y-4">
        {setupData.parties?.map((party) => {
          const partyTeamsList = partyTeams[party.id] || [];
          const isExpanded = expandedParties[party.id];
          
          return (
            <div key={party.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Party Header - Clickable to expand/collapse */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => togglePartyExpansion(party.id)}
                style={{ backgroundColor: `${party.color}15` }}
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5" style={{ color: party.color }} />
                    ) : (
                      <ChevronRight className="w-5 h-5" style={{ color: party.color }} />
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: party.color }}
                    />
                    <span 
                      className="px-2 py-1 text-xs font-bold rounded"
                      style={{ 
                        backgroundColor: party.color,
                        color: 'white'
                      }}
                    >
                      {party.code}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{party.name}</h3>
                      <p className="text-sm text-gray-500">{party.description}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {partyTeamsList.length} team{partyTeamsList.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addTeamToParty(party.id, party.name);
                    }}
                    className="flex items-center px-3 py-2 text-sm text-white rounded-lg hover:opacity-80 transition-opacity"
                    style={{ backgroundColor: party.color }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Team
                  </button>
                </div>
              </div>

              {/* Teams Section - Collapsible */}
              {isExpanded && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  {/* Teams for this party */}
                  {partyTeamsList.length > 0 ? (
                    <div className="space-y-3">
                      {partyTeamsList.map((team) => (
                        <div key={team.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                              <h4 className="font-medium text-gray-900">{team.name}</h4>
                            </div>
                            <button
                              onClick={() => removeTeamFromParty(party.id, team.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Team Permissions */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* File Permissions */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                File Permissions
                              </label>
                              <select
                                value={team.permissions.files}
                                onChange={(e) => updateTeamPermissions(party.id, team.id, 'files', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                {permissionTypes.map((perm) => (
                                  <option key={perm.id} value={perm.id}>
                                    {perm.name} - {perm.description}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Question Permissions */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Question Permissions
                              </label>
                              <select
                                value={team.permissions.questions}
                                onChange={(e) => updateTeamPermissions(party.id, team.id, 'questions', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="individual_only">Individual Only - Can only see own questions</option>
                                <option value="team_questions_only">Team Questions - Can see team questions</option>
                                <option value="party_all">Party All - Can see all questions in party</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                      <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No teams added yet</p>
                      <p className="text-sm text-gray-400">Click "Add Team" to create teams for this party</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 mb-3">
          Setup Summary
        </h4>
        <div className="space-y-2">
          {setupData.parties?.map((party) => {
            const teamCount = partyTeams[party.id]?.length || 0;
            return (
              <div key={party.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: party.color }}
                  />
                  <span className="text-gray-900 font-medium">{party.name}</span>
                  <span 
                    className="px-2 py-1 text-xs font-bold rounded"
                    style={{ 
                      backgroundColor: party.color,
                      color: 'white'
                    }}
                  >
                    {party.code}
                  </span>
                </div>
                <span className="text-gray-700">
                  {teamCount} team{teamCount !== 1 ? 's' : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Continue Button */}
      <div className="flex justify-end pt-6 border-t">
        <button
          onClick={handleContinue}
          disabled={loading || Object.values(partyTeams).some(teams => teams.length === 0)}
          className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating Teams...
            </>
          ) : (
            <>
              Continue to User Invitations
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TeamSetupStep;
